// 장비 등급·슬롯·강화·소비 아이템·펫·레이드·환생(정본: docs/CONTENT-90D-PRD.md 3B·4·5). 데이터만.
import type { AffixDef, BaseStats, ConsumableDef, EquipDef, EquipSlot, Grade, GradeDef, HeroId, LegendUniqueDef, NamedUniqueDef, PetDef, RaidDef, RebirthCycle, RebirthState, RegionNo, SetDef } from './types';

export const EQUIP_SLOTS: readonly EquipSlot[] = ['weapon', 'helm', 'armor', 'gloves', 'boots', 'amulet'];
/** 계정 공유 슬롯(무기만 영웅 전용, PRD 3) */
export const SHARED_SLOTS: readonly EquipSlot[] = ['helm', 'armor', 'gloves', 'boots', 'amulet'];

export const GRADES: readonly GradeDef[] = [
  { id: 'common', name: '일반', color: '#e8e8e8', mult: 1, affixes: [0, 0] },
  { id: 'rare', name: '희귀', color: '#4a8cff', mult: 1.25, affixes: [1, 2] },
  { id: 'epic', name: '영웅', color: '#a45cff', mult: 1.6, affixes: [2, 3] },
  { id: 'set', name: '세트', color: '#3fd07a', mult: 1.9, affixes: [2, 2] },
  { id: 'legend', name: '전설', color: '#f2b233', mult: 2.2, affixes: [3, 3] },
];

/** 버프 합산 캡(옵션·비약·펫 합, PRD 4.2) */
export const BUFF_CAP = { dropRate: 1, gold: 1, xp: 0.5, cooldown: 0.4, damageTaken: 0.5 } as const;

/**
 * 강화 +0~+15(PRD 4.3, 시뮬 c001 결정). to: 올라갈 단계, p: 기본 성공 확률, down: 실패 시 하락 확률.
 * 천장: 같은 단계 실패마다 기본 확률 +20%, 최대 3배. 하락해도 그 단계 카운터 보존. 파괴 없음.
 */
export const ENHANCE_TABLE: readonly { readonly to: number; readonly p: number; readonly down: number }[] = Array.from({ length: 15 }, (_, i) => {
  const s = i + 1;
  const lerp = (a: number, b: number, t: number) => Math.round((a + (b - a) * t) * 1000) / 1000;
  if (s <= 5) return { to: s, p: lerp(1, 0.8, (s - 1) / 4), down: 0 };
  if (s <= 9) return { to: s, p: lerp(0.7, 0.5, (s - 6) / 3), down: 0 };
  if (s <= 12) return { to: s, p: lerp(0.4, 0.3, (s - 10) / 2), down: 0 };
  return { to: s, p: lerp(0.2, 0.1, (s - 13) / 2), down: 0.5 };
});
export const ENHANCE_PITY = { step: 0.2, max: 3 } as const;
/** 보호 주문서 주간 공급: 주간 의뢰 3 + 레이드 3 + 탑 2 + 골드 상점 2 */
export const PROTECT_WEEKLY = { quest: 3, raid: 3, tower: 2, goldShop: 2 } as const;

export const CONSUMABLES: readonly ConsumableDef[] = [
  { id: 'hp_s', kind: 'potion', name: 'HP 물약(소)', effect: '최대 HP 15% 회복', cooldownGroup: 'potion', sellable: true },
  { id: 'hp_m', kind: 'potion', name: 'HP 물약(중)', effect: '최대 HP 30% 회복', cooldownGroup: 'potion', sellable: true },
  { id: 'hp_l', kind: 'potion', name: 'HP 물약(대)', effect: '최대 HP 50% 회복', cooldownGroup: 'potion', sellable: true },
  { id: 'mp_s', kind: 'potion', name: 'MP 물약(소)', effect: '최대 MP 20% 회복', cooldownGroup: 'potion', sellable: true },
  { id: 'mp_m', kind: 'potion', name: 'MP 물약(중)', effect: '최대 MP 40% 회복', cooldownGroup: 'potion', sellable: true },
  { id: 'mp_l', kind: 'potion', name: 'MP 물약(대)', effect: '최대 MP 70% 회복', cooldownGroup: 'potion', sellable: true },
  { id: 'elixir_str', kind: 'elixir', name: '힘의 비약', effect: '공격 +10%', durationSec: 1800, sellable: true },
  { id: 'elixir_wall', kind: 'elixir', name: '철벽의 비약', effect: '받는 피해 −8%', durationSec: 1800, sellable: true },
  { id: 'elixir_luck', kind: 'elixir', name: '행운의 비약', effect: '드롭률 +20%', durationSec: 1800, sellable: true },
  { id: 'elixir_wis', kind: 'elixir', name: '지혜의 비약', effect: 'EXP +20%', durationSec: 1800, sellable: true },
  { id: 'scroll_return', kind: 'scroll', name: '귀환 주문서', effect: '마을로 귀환', sellable: true },
  { id: 'feather_revive', kind: 'scroll', name: '부활 깃털', effect: '던전에서 1회 부활', sellable: true },
  { id: 'ticket_sweep', kind: 'scroll', name: '소탕권', effect: '3성 클리어 스테이지 즉시 정산', sellable: false },
  { id: 'food_basic', kind: 'food', name: '펫 먹이(일반)', effect: '펫 EXP +100', sellable: true },
  { id: 'food_fine', kind: 'food', name: '펫 먹이(고급)', effect: '펫 EXP +400', sellable: true },
  { id: 'food_royal', kind: 'food', name: '펫 먹이(특급)', effect: '펫 EXP +1,500', sellable: true },
  // 지역 보물상자(PRD 4.6): 구성 고정·내용 표기, 확률형 아님. 일회성 보상(스토리·업적)이라 D0a 주간 수급 시뮬에 넣지 않았다 — 수량은 c-quest에서
  ...([1, 2, 3, 4, 5, 6] as const).map((r): ConsumableDef => ({
    id: `box_r${r}`, kind: 'box', name: `지역${r} 보물상자`, effect: '열면 표기된 구성을 그대로 받는다', sellable: false,
    contents: [{ id: 'gold', count: 500 * r }, { id: 'stone', count: 2 * r }, { id: r <= 2 ? 'hp_s' : r <= 4 ? 'hp_m' : 'hp_l', count: 3 }],
  })),
];
/** 물약 규칙(PRD 4.6): 공용 쿨다운 6초, 개수 제한 없음, 연속 사용 시 회복량 −20%씩(10초 뒤 회복) */
export const POTION_RULE = { cooldownSec: 6, decayPerUse: 0.2, decayResetSec: 10 } as const;
/** 소탕권 무료 일일 지급(판매 안 함) */
export const SWEEP_DAILY_FREE = 3;

export const PETS: readonly PetDef[] = [
  { id: 'pet_moss', name: '이끼 정령', region: 1, buff: 'HP 재생', pickupRadius: 4, evoLv: [10, 20, 30] },
  { id: 'pet_ember', name: '잿불 여우', region: 2, buff: '골드 획득', pickupRadius: 4, evoLv: [10, 20, 30] },
  { id: 'pet_frost', name: '서리 올빼미', region: 3, buff: '치명 확률', pickupRadius: 4, evoLv: [10, 20, 30] },
  { id: 'pet_frog', name: '늪 개구리', region: 4, buff: '드롭률', pickupRadius: 4, evoLv: [10, 20, 30] },
  { id: 'pet_scarab', name: '모래 스카라브', region: 5, buff: '받는 피해 감소', pickupRadius: 4, evoLv: [10, 20, 30] },
  { id: 'pet_bat', name: '심연 박쥐', region: 6, buff: 'EXP 획득', pickupRadius: 4, evoLv: [10, 20, 30] },
];

// loot.gold·stones는 D0a 골드·재료 시뮬로 확정(2026-10-22): 레이드 1회 ≈ 그 레벨 사냥 10~15분 수입. legendChance는 전설 기대 수 검증에 쓴다
export const RAIDS: readonly RaidDef[] = [
  { id: 'raid_wyvern', name: '잿불 비룡', unlock: { town: 2 }, recLevel: 25, partySize: 4, weeklyLimit: 3, phases: [{ hpFrom: 1, patterns: ['화염 장판', '돌진'] }, { hpFrom: 0.5, patterns: ['비행 브레스', '화염 장판'] }], mercMechanics: ['장판 회피'], loot: { sets: ['set_wyvern'], legendChance: 0.05, protectFirstClear: 1, gold: 6000, stones: 30 } },
  { id: 'raid_giant', name: '서리 거인', unlock: { town: 4 }, recLevel: 40, partySize: 4, weeklyLimit: 3, phases: [{ hpFrom: 1, patterns: ['빙결 장판', '얼음 감옥'] }, { hpFrom: 0.5, patterns: ['지진', '얼음 감옥'] }], mercMechanics: ['장판 회피', '얼음 감옥 구출'], loot: { sets: ['set_wyvern', 'set_frost'], legendChance: 0.08, protectFirstClear: 1, gold: 20000, stones: 35 } },
  { id: 'raid_sandlord', name: '모래 군주의 화신', unlock: { regionClear: 5 }, recLevel: 65, partySize: 4, weeklyLimit: 3, phases: [{ hpFrom: 1, patterns: ['흡입', '모래 폭풍 시야 차단'] }, { hpFrom: 0.6, patterns: ['분신'] }, { hpFrom: 0.3, patterns: ['분신', '흡입', '모래 폭풍 시야 차단'] }], mercMechanics: ['장판 회피', '분신 처치'], loot: { sets: ['set_sand', 'set_dragon'], legendChance: 0.12, protectFirstClear: 1, gold: 70000, stones: 40 } },
  { id: 'raid_abyss', name: '심연의 용', unlock: { regionClear: 6 }, recLevel: 80, partySize: 4, weeklyLimit: 3, phases: [{ hpFrom: 1, patterns: ['꼬리 휩쓸기', '심연 브레스'] }, { hpFrom: 0.6, patterns: ['추락 운석', '꼬리 휩쓸기'] }, { hpFrom: 0.25, patterns: ['전멸기', '심연 브레스', '추락 운석'] }], mercMechanics: ['장판 회피', '탱 도발', '전멸기 피하기'], loot: { sets: ['set_dragon'], legendChance: 0.2, protectFirstClear: 1, gold: 150000, stones: 45 } },
];

/** 환생 회차와 모드(PRD 3B). released=false 인 모드는 환생 버튼 자체를 숨긴다 */
export const REBIRTH: readonly RebirthCycle[] = [
  { cycle: 0, mode: 'hackslash', name: '핵앤슬래시', released: true },
  { cycle: 1, mode: 'arpg', name: 'ARPG', released: true },
  // MMO 공개 전(released false)에는 2차 환생을 ARPG ★2로 연다(C12): 같은 필드·균열, 대균열 상한 +20, 재육성 ×0.25, 초월 +18 해금
  { cycle: 2, mode: 'mmo', name: 'MMORPG', released: false, fallback: { mode: 'arpg', name: 'ARPG ★2', released: true } },
];
/** 처음 상태의 영웅별 환생 진행(1회차 핵앤슬래시, 포인트·특전 없음) */
export function freshRebirth(heroId: HeroId): RebirthState {
  return { heroId, cycle: 0, mode: 'hackslash', points: 0, perks: [], rebornAt: null };
}
/** 환생 보상(PRD 3B) */
export const REBIRTH_REWARD = { statPoints: 10, skillSlotBonus: 1, nextCycleXpBonus: 0.5 } as const;
export function rebirthAvailable(level: number, cycle: number): boolean {
  const next = REBIRTH.find((r) => r.cycle === cycle + 1);
  return level >= 99 && !!next && next.released;
}

// ── 장비 정의(PRD 4) ───────────────────────────────

/** 등급별 최대 소켓(PRD 4.5) */
export const SOCKET_CAP: Readonly<Record<Grade, 0 | 1 | 2 | 3>> = { common: 0, rare: 1, epic: 2, set: 2, legend: 3 };

export const AFFIXES: readonly AffixDef[] = [
  { id: 'atkPct', name: '공격력 %', slots: ['weapon', 'gloves', 'amulet'], range: [0.03, 0.12] },
  { id: 'crit', name: '치명 확률', slots: ['weapon', 'gloves', 'helm', 'amulet'], range: [0.02, 0.06] },
  { id: 'critDmg', name: '치명 피해', slots: ['weapon', 'gloves', 'amulet'], range: [0.1, 0.35] },
  // 공격 속도: 동작 프레임 데이터(공격·스킬 타임라인 틱)를 배율로 늘이는 작업이 필요해 아직 효과가 없다 → 드롭 옵션 풀에서 뺀다(slots 비움, PR #154 Codex P1).
  // 보이는데 효과 없는 옵션을 굴리지 않는다. 효과를 연결하면 slots를 ['weapon', 'gloves']로 되돌린다
  { id: 'atkSpd', name: '공격 속도', slots: [], range: [0.03, 0.1] },
  { id: 'moveSpd', name: '이동 속도', slots: ['boots'], range: [0.03, 0.1] },
  { id: 'skillDmg', name: '스킬 피해', slots: ['weapon', 'helm', 'amulet'], range: [0.04, 0.15] },
  { id: 'cooldown', name: '재사용 감소', slots: ['helm', 'amulet'], range: [0.02, 0.08] },
  { id: 'lifesteal', name: '흡혈', slots: ['weapon'], range: [0.005, 0.02] },
  { id: 'hpPct', name: '최대 HP %', slots: ['helm', 'armor', 'boots'], range: [0.04, 0.12] },
  { id: 'def', name: '방어', slots: ['helm', 'armor', 'gloves', 'boots'], range: [0.03, 0.1] },
  { id: 'damageTaken', name: '받는 피해 감소', slots: ['armor', 'amulet'], range: [0.02, 0.06] },
  { id: 'dropRate', name: '드롭률', slots: ['helm', 'amulet'], range: [0.05, 0.2] },
  { id: 'gold', name: '골드 획득', slots: ['boots', 'amulet'], range: [0.05, 0.2] },
  { id: 'xp', name: 'EXP 획득', slots: ['amulet'], range: [0.03, 0.1] },
  { id: 'mpRegen', name: 'MP 재생', slots: ['armor', 'amulet'], range: [0.05, 0.2] },
  { id: 'awakenGauge', name: '각성 게이지', slots: ['weapon', 'amulet'], range: [0.03, 0.1] },
];
export function affixesFor(slot: EquipSlot): AffixDef[] { return AFFIXES.filter((a) => a.slots.includes(slot)); }

/**
 * 지역 세트 6 + 레이드 2(PRD 4.2). 2·4세트 효과는 장착 부위 수로 shared/gear.ts sumGear가 실제로 적용한다(fx2·fx4).
 * 4세트 "스킬 변형"(덩굴 속박·화상·빙결 고리·흡입 범위·모래 회오리)은 스킬 타임라인·VFX 작업이 필요해 Day-0은 같은 결의 수치 효과로 두고
 * 문구도 실제 효과로 바꿨다(보이는데 적용 안 되는 효과 없음, PR #154 Codex P1). 스킬 변형은 후속(PRD 4.2 주석).
 */
export const SETS: readonly SetDef[] = [
  { id: 'set_moss', name: '이끼 파수꾼', region: 1, pieces: ['helm', 'armor', 'gloves', 'boots'], bonus2: '최대 HP +8%', bonus4: '기본 공격 피해 +20%', fx2: { hpPct: 0.08 }, fx4: { basicDmg: 0.2 } },
  { id: 'set_ember', name: '잿불 전초', region: 2, pieces: ['helm', 'armor', 'gloves', 'boots'], bonus2: '공격 +6%', bonus4: '스킬 피해 +12%', fx2: { atkPct: 0.06 }, fx4: { skillDmg: 0.12 } },
  { id: 'set_frost', name: '서리 묘지기', region: 3, pieces: ['helm', 'armor', 'gloves', 'boots'], bonus2: '받는 피해 −5%', bonus4: '재사용 대기 −8%', fx2: { damageTaken: 0.05 }, fx4: { cooldown: 0.08 } },
  { id: 'set_swamp', name: '늪의 사제', region: 4, pieces: ['helm', 'armor', 'gloves', 'boots'], bonus2: 'MP 재생 +15%', bonus4: '흡혈 +1.5%', fx2: { mpRegen: 0.15 }, fx4: { lifesteal: 0.015 } },
  { id: 'set_sand', name: '모래 군주', region: 5, pieces: ['helm', 'armor', 'gloves', 'boots'], bonus2: '치명 +4%', bonus4: '치명 피해 +30%', fx2: { crit: 0.04 }, fx4: { critDmg: 0.3 } },
  { id: 'set_abyss', name: '심연 집행', region: 6, pieces: ['helm', 'armor', 'gloves', 'boots'], bonus2: '스킬 피해 +8%', bonus4: '각성 게이지 +25%', fx2: { skillDmg: 0.08 }, fx4: { awakenGauge: 0.25 } },
  { id: 'set_wyvern', name: '비룡 사냥꾼', region: 'raid', pieces: ['helm', 'armor', 'gloves', 'boots'], bonus2: '보스 피해 +8%', bonus4: '그로기 게이지 피해 +30%', fx2: { bossDmg: 0.08 }, fx4: { staggerDmg: 0.3 } },
  { id: 'set_dragon', name: '심연룡의 비늘', region: 'raid', pieces: ['helm', 'armor', 'gloves', 'boots'], bonus2: '받는 피해 −8%', bonus4: '쓰러질 피해를 판마다 1회 HP 1로 버팀', fx2: { damageTaken: 0.08 }, fx4: { lastStand: 1 } },
];

/**
 * 전설 고유 효과(PRD 4.2 "3 + 고유 효과", PR #154 Codex 2차 P1). PRD에 이름 목록이 없어 c009에서 정했다: 부위마다 2개 후보, 전설 드롭 때
 * 그 부위 후보 중 하나를 굴려 개체에 효과 id(uniq)로 저장한다. 수치는 지금 전투 규칙에 바로 붙는 것만(새 VFX 없음, Ev.GearUnique 훅).
 * fx 단위: lowHpDmg·eliteDmg 피해 배율 가산, critRefund 치명타 때 모든 스킬 재사용 대기 감소 틱, dodgeShield 최대 HP 비율 보호막(3초),
 * killHeal 최대 HP 비율 회복, killMp MP 회복량, killAwaken 각성 게이지 가산(0..1).
 */
export const LEGEND_UNIQUES: readonly LegendUniqueDef[] = [
  { id: 'u_lastgasp', name: '벼랑 끝 칼날', desc: 'HP 30% 이하일 때 주는 피해 +25%', slots: ['weapon'], fx: { lowHpDmg: 0.25 } },
  { id: 'u_tyrant', name: '폭군 사냥', desc: '엘리트·보스에게 주는 피해 +15%', slots: ['weapon', 'gloves'], fx: { eliteDmg: 0.15 } },
  { id: 'u_tempo', name: '칼날의 박자', desc: '치명타를 넣으면 모든 스킬 재사용 대기 0.2초 감소', slots: ['gloves', 'amulet'], fx: { critRefund: 12 } },
  { id: 'u_aegis', name: '회피의 방패', desc: '회피하면 3초간 최대 HP 8% 보호막', slots: ['armor', 'boots'], fx: { dodgeShield: 0.08 } },
  { id: 'u_harvest', name: '피의 수확', desc: '적을 처치하면 최대 HP 1% 회복', slots: ['helm', 'armor'], fx: { killHeal: 0.01 } },
  { id: 'u_wellspring', name: '샘솟는 마력', desc: '적을 처치하면 MP 4 회복', slots: ['helm', 'amulet'], fx: { killMp: 4 } },
  { id: 'u_fervor', name: '투지', desc: '적을 처치하면 각성 게이지 +0.6%', slots: ['boots'], fx: { killAwaken: 0.006 } },
];
/**
 * 이름 있는 유니크(docs/BALANCE-1028.md 3절, 대표님 2026-10-28 "초당 MP 회복 같은 효과로 인기 있고 비싸게 팔리는 템"). 원작 명칭·그림을 쓰지 않는 오리지널 이름.
 * 드롭: 그 지역 그 보스(중간보스 n-5·지역보스 n-10)를 잡을 때 source.chance(난이도 무관, 드롭률 옵션 영향 없음)로 1개 — 보스 확정 장비와 따로 굴린다.
 * 개체는 영웅(보라) 등급 능력치 + 굴린 옵션 + 고정 효과(fx), 저장은 GearItem.nu(선택 필드). 같은 유니크 두 개를 껴도 효과는 한 번.
 * 시세 기준값 marketBase = 그 지역 클리어 골드(REGION_GOLD.clear) × 40. 경매장·거래 규칙은 레인 D가 이 값과 tradable로 만든다.
 */
export const NAMED_UNIQUES: readonly NamedUniqueDef[] = [
  { id: 'nu_echo_robe', name: '메아리 수련복', slot: 'armor', desc: ['평타로 얻는 MP +50%'], fx: { basicMp: 0.5 },
    source: { region: 1, boss: 'final', chance: 0.03 }, tradable: true, marketBase: 8000, lore: '고목 수호자의 숲에서 수련하던 이의 옷. 칼끝이 닿을 때마다 숨이 고른다.' },
  { id: 'nu_sage_band', name: '현자의 손목띠', slot: 'gloves', desc: ['초당 MP 회복 +2.5'], fx: { mpFlat: 2.5 },
    source: { region: 2, boss: 'final', chance: 0.02 }, tradable: true, marketBase: 20000, lore: '잿불 대주술사가 지니던 손목띠. 마력이 쉬지 않고 스며든다.' },
  { id: 'nu_still_spring', name: '고요한 샘 목걸이', slot: 'amulet', desc: ['스킬 MP 소모 −12%'], fx: { mpCostCut: 0.12 },
    source: { region: 3, boss: 'mid', chance: 0.02 }, tradable: true, marketBase: 36000, lore: '묘지기 기사가 지키던 얼음 샘의 물방울을 담았다.' },
  { id: 'nu_mist_step', name: '물안개 걸음', slot: 'boots', desc: ['초당 MP 회복 +2', '이동 속도 +6%'], fx: { mpFlat: 2, moveSpd: 0.06 },
    source: { region: 4, boss: 'final', chance: 0.015 }, tradable: true, marketBase: 68000, lore: '늪의 히드라 둥지 바닥에서 건진 장화. 발밑에 늘 안개가 핀다.' },
  { id: 'nu_star_circlet', name: '별무리 머리띠', slot: 'helm', desc: ['최대 MP +60', '초당 MP 회복 +1.5'], fx: { maxMpFlat: 60, mpFlat: 1.5 },
    source: { region: 5, boss: 'mid', chance: 0.015 }, tradable: true, marketBase: 124000, lore: '폐도 경비대장의 투구 안쪽에 감겨 있던 별빛 천.' },
  { id: 'nu_void_scribe', name: '심연 서기의 장갑', slot: 'gloves', desc: ['스킬 MP 소모 −15%', '스킬 피해 +5%'], fx: { mpCostCut: 0.15, skillDmg: 0.05 },
    source: { region: 6, boss: 'final', chance: 0.01 }, tradable: true, marketBase: 228000, lore: '심연의 군주 곁에서 주문을 받아 적던 서기의 장갑.' },
];
/** 스킬 MP 소모 감소 합 상한(유니크 두 개를 겹쳐도 25%) */
export const MP_COST_CUT_CAP = 0.25;
/** 이름 있는 유니크 글자 색(툴팁·바닥 빛, 전설 금색과 구분되는 주황) */
export const NAMED_COLOR = '#ff8c2e';
export function namedUniqueDef(id: string | undefined): NamedUniqueDef | undefined { return id ? NAMED_UNIQUES.find((u) => u.id === id) : undefined; }
/** 그 지역·보스 종류에서 굴리는 유니크 목록 */
export function namedFor(region: number, boss: 'mid' | 'final'): NamedUniqueDef[] { return NAMED_UNIQUES.filter((u) => u.source.region === region && u.source.boss === boss); }

/** 보호막 지속(틱, 3초) */
export const UNIQUE_SHIELD_TICKS = 180;
/** 부위의 고유 효과 후보 */
export function uniquesFor(slot: EquipSlot): LegendUniqueDef[] { return LEGEND_UNIQUES.filter((u) => u.slots.includes(slot)); }

/**
 * 장비 기본 능력치 성장(아이템 레벨당 배율, D0a 피해 시뮬 결정). 이전 가안(공격 ×1.09/레벨)은 Lv99 무기 기본 공격이 4만을 넘어
 * 지역5 장비(전설 +12)에서 이미 피해 상한 99,999의 약 2배였다 → 헬 장비(ilvl 88) 전설 +13부터 상한에 닿게 낮췄다.
 * 1.047 → 1.049(PR #153 Codex P1): 최대 1타 계산이 각성 난무에 연계 ×1.35를 잘못 곱해(게임 Combat.linkMult는 각성 1) Lv90 +13이
 * 105%로 보였는데 실제는 89%였다. 1.049면 Lv90 헬 전설 +13 104%, 하드 전부 전설 +15는 84%(1.0495면 87%, 1.048이면 +13이 96%로 못 닿음).
 * 공격·HP는 같은 비율로 자라 몹 HP·피해의 지역 배율(world.ts REGION_HP_MULT)과 짝을 이룬다.
 */
export const GEAR_GROWTH = { attack: 1.049, maxHp: 1.049, def: 1.035 } as const;
/** 부위·아이템 레벨 → 기본 능력치(등급 배율·강화 전). 일반 등급 정의와 드롭 개체가 같은 곡선을 쓴다 */
export function gearBase(slot: EquipSlot, ilvl: number): BaseStats {
  const a = Math.pow(GEAR_GROWTH.attack, ilvl - 1), h = Math.pow(GEAR_GROWTH.maxHp, ilvl - 1), d = Math.pow(GEAR_GROWTH.def, ilvl - 1);
  switch (slot) {
    case 'weapon': return { attack: Math.round(10 * a) };
    case 'helm': return { maxHp: Math.round(20 * h), def: Math.round(2 * d) };
    case 'armor': return { maxHp: Math.round(40 * h), def: Math.round(4 * d) };
    case 'gloves': return { attack: Math.round(3 * a), def: Math.round(1 * d) };
    case 'boots': return { maxHp: Math.round(15 * h), def: Math.round(2 * d) };
    case 'amulet': return { crit: Math.round(1 + ilvl / 25) / 100 };
  }
}
/** gearBase와 같은 값(없는 칸은 0)을 out에 채운다(할당 없음, 장비 합계 sumGear용) */
export function gearBaseInto(out: { attack: number; maxHp: number; def: number; crit: number }, slot: EquipSlot, ilvl: number): void {
  const a = Math.pow(GEAR_GROWTH.attack, ilvl - 1), h = Math.pow(GEAR_GROWTH.maxHp, ilvl - 1), d = Math.pow(GEAR_GROWTH.def, ilvl - 1);
  out.attack = 0; out.maxHp = 0; out.def = 0; out.crit = 0;
  switch (slot) {
    case 'weapon': out.attack = Math.round(10 * a); break;
    case 'helm': out.maxHp = Math.round(20 * h); out.def = Math.round(2 * d); break;
    case 'armor': out.maxHp = Math.round(40 * h); out.def = Math.round(4 * d); break;
    case 'gloves': out.attack = Math.round(3 * a); out.def = Math.round(1 * d); break;
    case 'boots': out.maxHp = Math.round(15 * h); out.def = Math.round(2 * d); break;
    case 'amulet': out.crit = Math.round(1 + ilvl / 25) / 100; break;
  }
}
const BASE_SLOT: Readonly<Record<EquipSlot, (lv: number) => BaseStats>> = {
  weapon: (lv) => gearBase('weapon', lv), helm: (lv) => gearBase('helm', lv), armor: (lv) => gearBase('armor', lv),
  gloves: (lv) => gearBase('gloves', lv), boots: (lv) => gearBase('boots', lv), amulet: (lv) => gearBase('amulet', lv),
};
const HEROES: readonly HeroId[] = ['kael', 'lyra', 'seren', 'brann'];
const SLOT_NAME: Readonly<Record<EquipSlot, string>> = { weapon: '무기', helm: '투구', armor: '갑옷', gloves: '장갑', boots: '신발', amulet: '목걸이' };
/** 영웅 전용 무기 이름(플레이어 문구, heroes.ts weapon과 같다. 순환 import를 피해 값으로 둔다) */
const HERO_WEAPON: Readonly<Record<HeroId, string>> = { kael: '대검', lyra: '장궁', seren: '지팡이', brann: '룬 해머', lumen: '성광 지팡이' };

/**
 * 지역마다 일반 베이스 6부위(무기는 영웅 4명 각각) + 세트 4부위. 정의의 grade·sockets는 그 정의 그대로의 값이다
 * (일반 베이스 = 일반 등급·소켓 0, 세트 = 세트 등급·소켓 2). 드롭 개체는 등급을 다시 굴리고 소켓 = SOCKET_CAP[굴린 등급](c007).
 * 기본 능력치 곡선(gearBase)은 D0a 피해 시뮬 확정. 드롭 개체의 아이템 레벨·등급 굴림은 c009.
 */
export const EQUIPS: readonly EquipDef[] = (() => {
  const out: EquipDef[] = [];
  for (let r = 1 as RegionNo; r <= 6; r = (r + 1) as RegionNo) {
    const level = [1, 18, 30, 40, 55, 68][r - 1];
    const tier = (r <= 2 ? 1 : r <= 4 ? 2 : 3) as 1 | 2 | 3;
    for (const slot of ['weapon', 'helm', 'armor', 'gloves', 'boots', 'amulet'] as const) {
      const pool = affixesFor(slot).map((a) => a.id);
      if (slot === 'weapon') {
        for (const h of HEROES) out.push({ id: `eq_r${r}_${slot}_${h}`, name: `지역${r} ${HERO_WEAPON[h]}`, slot, heroLock: h, grade: 'common', level, region: r, base: BASE_SLOT[slot](level), affixPool: pool, sockets: SOCKET_CAP.common, tier });
      } else {
        out.push({ id: `eq_r${r}_${slot}`, name: `지역${r} ${SLOT_NAME[slot]}`, slot, grade: 'common', level, region: r, base: BASE_SLOT[slot](level), affixPool: pool, sockets: SOCKET_CAP.common, tier });
      }
    }
    const set = SETS[r - 1];
    for (const slot of set.pieces) out.push({ id: `eq_${set.id}_${slot}`, name: `${set.name} ${SLOT_NAME[slot]}`, slot, grade: 'set', level: level + 5, region: r, base: BASE_SLOT[slot](level + 5), affixPool: affixesFor(slot).map((a) => a.id), setId: set.id, sockets: SOCKET_CAP.set, tier });
  }
  // 레이드 전용 세트(RaidLoot.sets가 고르는 실물): 그 세트를 주는 레이드 중 가장 낮은 권장 레벨, 그 레벨이 속한 지역 단계(PR #145 Codex 8차)
  const STARTS = [1, 18, 30, 40, 55, 68];
  for (const set of SETS.filter((x) => x.region === 'raid')) {
    const level = Math.min(...RAIDS.filter((rd) => rd.loot.sets.includes(set.id)).map((rd) => rd.recLevel));
    const r = (STARTS.filter((st) => st <= level).length) as RegionNo;
    const tier = (r <= 2 ? 1 : r <= 4 ? 2 : 3) as 1 | 2 | 3;
    for (const slot of set.pieces) out.push({ id: `eq_${set.id}_${slot}`, name: `${set.name} ${SLOT_NAME[slot]}`, slot, grade: 'set', level, region: r, base: BASE_SLOT[slot](level), affixPool: affixesFor(slot).map((a) => a.id), setId: set.id, sockets: SOCKET_CAP.set, tier });
  }
  return out;
})();
